<!--
  =====================================================================
  SECTION INFORMATIVE GRID - INFORMATIVE ITEMS LIST
  =====================================================================
  
  Grid layout displaying informative page items.
  
  FEATURES:
  - Responsive grid with eyebrow, title, description, link columns
  - Progressive text extraction from sections
  - Hover effects with color change
  - Click navigation to full page
  - Automatically styled link indicators
  
  BEHAVIOR:
  - Extracts first 150 chars of first html-text section as description
  - Links appear as expandable arrows on hover
  - Grid adapts to viewport width
  
  =====================================================================
-->

<template>
  <section class="informative-list">
    <div 
      v-for="item in content?.items || []" 
      :key="item.id" 
      class="informative-item" 
      @click="handleInformativaClick($event, item.id)"
    >
      <div class="informative-eyebrow">{{ item.eyebrow }}</div>
      <h3 class="informative-title">{{ item.title }}</h3>
      <p class="informative-description">{{ getDescription(item) }}</p>
      <a 
        :href="getInformativaLink(item.id)" 
        class="informative-link" 
        @click.stop="handleInformativaClick($event, item.id)"
      >
        Consulta
        <span class="arrow">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </a>
    </div>
  </section>
</template>

<script setup>
import { getInformativaLinkById } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} content - Content object with items array
 */
defineProps({
  content: {
    type: Object,
    required: true,
  },
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION & LINK UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Gets informative page URL by ID
 * @param {String|Number} id - Item ID
 * @returns {String} Informative page URL
 */
function getInformativaLink(id) {
  return getInformativaLinkById(id)
}

/**
 * Handles informative item click
 * Navigates to full page
 * @param {Event} event - Click event
 * @param {String|Number} id - Item ID
 */
function handleInformativaClick(event, id) {
  event.preventDefault()
  const url = getInformativaLink(id)
  window.location.href = url
}

// ───────────────────────────────────────────────────────────────────
// TEXT UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts description text from item
 * Gets first html-text section and strips HTML tags
 * Truncates to 150 characters
 * @param {Object} item - Informative item object
 * @returns {String} Plain text description
 */
function getDescription(item) {
  const firstSection = item.sections?.find(section => section.type === 'html-text')
  if (!firstSection?.content?.text) return ''
  
  const textOnly = firstSection.content.text.replace(/<[^>]*>/g, '').trim()
  return textOnly.length > 150 ? textOnly.substring(0, 150) + '...' : textOnly
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ------------------------------------------------------------------
   Container principale - Lista informative
------------------------------------------------------------------ */
.informative-list {
  width: 100%;
  max-width: 100vw;
  padding: 0 $container-padding-x;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* ------------------------------------------------------------------
   Item informativa - Layout a griglia con colonna eyebrow leggermente più ampia
------------------------------------------------------------------ */
.informative-item {
  display: grid;
  grid-template-columns: clamp(200px, 25vw, 280px) clamp(350px, 38vw, 450px) 1fr auto;
  grid-template-areas: "eyebrow title description link";
  align-items: center;
  gap: clamp(1rem, 3vw, 2rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) 0;
  border-bottom: 1px solid $clr-border;
  transition: background-color 0.25s ease, transform 0.2s ease;
  cursor: pointer !important;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-bg-light, 0.6);
      transform: translateX(4px);

      .informative-title { color: $clr-tertiary; }
      .informative-link  { color: $clr-tertiary; transform: translateX(4px); }
    }
  }

  &:last-child { border-bottom: none; }

  .informative-eyebrow {
    grid-area: eyebrow;
    font-size: $font-list-meta;
    color: $clr-tertiary;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
    min-width: 0;
    margin: 0;
    cursor: pointer !important;
  }

  .informative-title {
    grid-area: title;
    font-size: $font-list-title;
    font-weight: 600;
    color: $text-color;
    line-height: 1.4;
    margin: 0;
    transition: color 0.25s ease;
    min-width: 0;
    overflow: hidden;
    @include line-clamp(2, 1.4, $overflow: false);
    cursor: pointer !important;
  }

  .informative-description {
    grid-area: description;
    font-size: $font-list-meta;
    color: $clr-text-light;
    line-height: 1.5;
    margin: 0;
    text-align: left;
    min-width: 0;
    overflow: hidden;
    @include line-clamp(2, 1.5, $overflow: false);
    cursor: pointer !important;
  }

  .informative-link {
    grid-area: link;
    font-size: $font-list-meta;
    color: $clr-brand-dark;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    transition: color 0.25s ease, transform 0.25s ease;
    justify-self: end;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.5rem);
    line-height: 1;
    cursor: pointer !important;
  }

  @media (hover: hover) {
    .informative-link:hover {
      color: $clr-tertiary;
      transform: translateX(4px);
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Desktop medio (1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .informative-list {
    padding: 0 $container-padding-x-mob;
  }

  .informative-item {
    grid-template-columns: clamp(190px, 25vw, 260px) clamp(320px, 35vw, 400px) 1fr auto;
    gap: clamp(0.8rem, 2.5vw, 1.8rem);
    padding: clamp(0.9rem, 2.2vw, 1.3rem) 0;
  }
}

/* ------------------------------------------------------------------
   Responsive - Tablet (1024px)
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .informative-item {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "eyebrow title link"
      "description description description";
    gap: $space-0-75;
    padding: $space-1 0;

    .informative-description {
      margin-top: $space-0-5;
      grid-column: 1 / -1;
    }

    .informative-eyebrow {
      min-width: clamp(190px, 25vw, 260px);
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Mobile (768px)
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .informative-item {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "eyebrow link"
      "title title"
      "description description";
    gap: $space-0-5;
    padding: $space-0-75 0;

    .informative-title,
    .informative-description {
      margin-top: $space-0-25;
      grid-column: 1 / -1;
    }
  }
}

/* ------------------------------------------------------------------
   Responsive - Extra small (640px)
------------------------------------------------------------------ */
@media (max-width: 640px) {
  .informative-item {
    grid-template-columns: 1fr;
    grid-template-areas:
      "eyebrow"
      "title"
      "description"
      "link";
    gap: $space-0-5;
    padding: $space-0-75 0;

    .informative-link {
      justify-self: start;
      margin-top: $space-0-25;
    }
  }
}

/* Freccia vettoriale con rotazione e dimensioni come News */
.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.2em;
  height: 1.2em;
  color: currentColor;
  transition: transform 0.3s ease;
  transform: rotate(-90deg);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

@media (hover: hover) {
  .informative-link:hover .arrow {
    transform: rotate(90deg);
  }
}
</style>